import React, { useCallback, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import { toPng } from "html-to-image";
import { downloadPngDataUrl } from "../../admin/welcome/downloadPngDataUrl";
import { prepareWebAdCaptureNode } from "./WebAdJigsawBoard";
import { WebAdShotArt } from "./WebGoogleAdShots";
import {
    WEB_AD_FRAME,
    findWebAdShot,
    webAdShotQueryPath,
    webAdShotsByGroup,
} from "./webAdShots";

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

const PREVIEW_MAX_W = 920;

export default function WebGoogleAdFramePage() {
    const [params] = useSearchParams();
    const shot = useMemo(
        () => findWebAdShot(params.get("asset"), params.get("shot")),
        [params],
    );
    const frame = WEB_AD_FRAME[shot.asset] || WEB_AD_FRAME.landscape;
    const frameRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [exportLite, setExportLite] = useState(false);
    const [saveError, setSaveError] = useState(null);

    const fileName = `${shot.fileStem}-${frame.width}x${frame.height}.png`;
    const previewScale = Math.min(1, PREVIEW_MAX_W / frame.width);
    const isJigsaw = shot.id === "jigsaw";

    const handleDownload = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        if (isJigsaw) {
            setExportLite(true);
            for (let i = 0; i < 40; i += 1) {
                await new Promise((resolve) =>
                    requestAnimationFrame(() => requestAnimationFrame(resolve)),
                );
                const images = node.querySelectorAll(
                    "[data-web-ad-jigsaw] image[href], [data-web-ad-jigsaw] image",
                );
                if (images.length > 0 && images.length <= 3) break;
                await new Promise((resolve) => setTimeout(resolve, 30));
            }
        }
        try {
            await prepareWebAdCaptureNode(node);
            const dataUrl = await toPng(node, {
                ...TO_PNG_OPTS,
                width: frame.width,
                height: frame.height,
                backgroundColor: "#F8FAFC",
                onImageErrorHandler: () => undefined,
            });
            await downloadPngDataUrl(dataUrl, fileName);
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setExportLite(false);
            setSaving(false);
        }
    }, [fileName, frame.height, frame.width, isJigsaw, saving]);

    return (
        <Box
            sx={{
                minHeight: "100vh",
                bgcolor: "#0F172A",
                py: { xs: 2, sm: 3 },
                px: { xs: 1.5, sm: 3 },
                boxSizing: "border-box",
            }}
        >
            <Box sx={{ maxWidth: 1100, mx: "auto" }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: "#F8FAFC", mb: 0.5 }}>
                    Web Google Ads — {shot.label}
                </Typography>
                <Typography variant="body2" sx={{ color: "#94A3B8", mb: 2 }}>
                    {frame.label} · {frame.ratio} · {frame.width}×{frame.height} px · max 5 MB · English
                    {isJigsaw
                        ? " · live jigsaw from play-jigsaw story"
                        : shot.id === "word-wheel"
                          ? " · live word wheel from tpl"
                          : shot.id === "crossword"
                            ? " · live crossword from play-cross"
                            : shot.id?.startsWith("tutor-")
                              ? " · live tutor story The Gallery Deception"
                              : " · sample art (not live templates)"}
                </Typography>

                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 2 }}>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveAltIcon />}
                        onClick={handleDownload}
                        disabled={saving}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                        Download PNG
                    </Button>
                    <Typography variant="caption" sx={{ color: "#64748B", alignSelf: "center" }}>
                        {fileName}
                    </Typography>
                </Stack>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ display: "block", mb: 1.5 }}>
                        {saveError}
                    </Typography>
                ) : null}

                <Box
                    sx={{
                        width: frame.width * previewScale,
                        height: frame.height * previewScale,
                        mb: 3,
                        boxShadow: "0 24px 64px rgba(0,0,0,0.45)",
                        borderRadius: 1,
                        overflow: "hidden",
                        pointerEvents: "none",
                    }}
                >
                    <Box
                        sx={{
                            width: frame.width,
                            height: frame.height,
                            transform: `scale(${previewScale})`,
                            transformOrigin: "top left",
                        }}
                    >
                        <WebAdShotArt shot={shot} width={frame.width} height={frame.height} />
                    </Box>
                </Box>

                {/* 1:1 capture target — html-to-image misreads CSS-scaled nodes */}
                <Box
                    aria-hidden
                    sx={{
                        position: "fixed",
                        left: -10000,
                        top: 0,
                        width: frame.width,
                        height: frame.height,
                        overflow: "hidden",
                        pointerEvents: "none",
                    }}
                >
                    <WebAdShotArt
                        shot={shot}
                        width={frame.width}
                        height={frame.height}
                        frameRef={frameRef}
                        exportLite={exportLite}
                    />
                </Box>

                <Typography variant="caption" sx={{ color: "#64748B", display: "block", mb: 1 }}>
                    All shots
                </Typography>
                <Stack spacing={1.25} sx={{ mb: 2 }}>
                    {webAdShotsByGroup().map(({ group, shots }) => (
                        <Box key={group}>
                            <Typography
                                variant="caption"
                                sx={{ color: "#94A3B8", fontWeight: 700, display: "block", mb: 0.35 }}
                            >
                                {group}
                            </Typography>
                            <Stack spacing={0.35}>
                                {shots.map((item) => {
                                    const path = webAdShotQueryPath(item);
                                    const active = item.id === shot.id && item.asset === shot.asset;
                                    return (
                                        <Link
                                            key={`${item.asset}-${item.id}`}
                                            component={RouterLink}
                                            to={path}
                                            underline="hover"
                                            sx={{
                                                color: active ? "#38BDF8" : "#CBD5E1",
                                                fontWeight: active ? 700 : 500,
                                                fontSize: 14,
                                            }}
                                        >
                                            {item.label}
                                            <Box component="span" sx={{ color: "#64748B", ml: 1, fontWeight: 400 }}>
                                                {WEB_AD_FRAME[item.asset].width}×{WEB_AD_FRAME[item.asset].height}
                                            </Box>
                                        </Link>
                                    );
                                })}
                            </Stack>
                        </Box>
                    ))}
                </Stack>
            </Box>
        </Box>
    );
}
